import React from 'react';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ANALYTICS_ORIGIN } from '../../constants/snippetAnalytics.ts';
import type { Snippet } from '../../types/snippet.ts';
import { SnippetMoreActions } from '../../common/SnippetMoreActions.tsx';
import { SnippetCreatedByAvatar } from '../../common/SnippetCreatedByAvatar.tsx';
import { SnippetFavoriteButton } from '../../common/SnippetFavoriteButton.tsx';
import { SnippetUsageAndPrivateLabels } from '../../common/SnippetUsageAndPrivateLabels.tsx';
import { shouldShowUsageAndPrivateLabels } from '../../utils/insertCountUtils.ts';

const contentStyles = cssMap({
	upperContainer: {
		flexBasis: token('space.400'),
	},
	titleContainer: {
		flex: 1,
		textAlign: 'left',
	},
	titleContainerFilled: {
		width: '100%',
	},
	actionsContainer: {
		pointerEvents: 'auto',
		flexShrink: 0,
	},
});

export type SnippetPanelItemContentProps = {
	snippet: Snippet;
	isHovered: boolean;
	showActions: boolean;
	showPanelActions: boolean;
	onMenuOpenChange: (isOpen: boolean) => void;
	contentId: string;
	spaceId: string;
	spaceKey: string;
};

export const SnippetPanelItemContent = ({
	snippet,
	isHovered,
	showActions,
	showPanelActions,
	onMenuOpenChange,
	contentId,
	spaceId,
	spaceKey,
}: SnippetPanelItemContentProps): React.JSX.Element => {
	const analyticsOrigin = ANALYTICS_ORIGIN.SNIPPET_PANEL;
	const shouldFillContainer = !isHovered;
	const showUsageAndPrivateLabels = shouldShowUsageAndPrivateLabels(snippet);

	return (
		<Stack alignInline="start" alignBlock="start" space="space.075" grow="fill">
			<Inline xcss={contentStyles.upperContainer} alignBlock="center" space="space.050" grow="fill">
				<Box
					xcss={cx(
						contentStyles.titleContainer,
						shouldFillContainer && contentStyles.titleContainerFilled,
					)}
				>
					<Heading size="xsmall">
						<Text maxLines={2} weight="bold">
							{snippet.name}
						</Text>
					</Heading>
				</Box>
				{showActions && (
					<Inline xcss={contentStyles.actionsContainer} alignBlock="center" space="space.050">
						{showPanelActions && (
							<SnippetMoreActions
								analyticsOrigin={analyticsOrigin}
								snippet={snippet}
								isHovered={isHovered}
								onMenuOpenChange={onMenuOpenChange}
								contentId={contentId}
								spaceId={spaceId}
								spaceKey={spaceKey}
							/>
						)}
						<SnippetFavoriteButton
							analyticsOrigin={analyticsOrigin}
							snippet={snippet}
							isHovered={isHovered}
						/>
					</Inline>
				)}
			</Inline>
			<Inline alignBlock="center" separator="•" space="space.075">
				<Inline alignInline="center" alignBlock="center" space="space.050">
					<SnippetCreatedByAvatar snippetOwner={snippet.createdBy} />
					<Text color="color.text.subtlest" size="small" maxLines={1}>
						{snippet.createdBy.displayName}
					</Text>
				</Inline>
				{showUsageAndPrivateLabels && <SnippetUsageAndPrivateLabels snippet={snippet} />}
			</Inline>
		</Stack>
	);
};
